import React, { useState, useEffect, useRef } from 'react';
import { Search, X, Loader } from 'lucide-react';

const SearchBar = ({ 
  searchTerm, 
  onSearch, 
  placeholder = "Postcode or Location",
  loading = false,
  onClear
}) => {
  // Use local state to handle input changes immediately
  const [localValue, setLocalValue] = useState(searchTerm);
  const debounceTimeoutRef = useRef(null);

  // Sync local value with prop when it changes externally
  useEffect(() => {
    setLocalValue(searchTerm);
  }, [searchTerm]);

  const handleSubmit = (e) => {
    e.preventDefault();
    // Clear any pending debounced calls and search immediately
    if (debounceTimeoutRef.current) {
      clearTimeout(debounceTimeoutRef.current);
    }
    if (onSearch) {
      onSearch(localValue);
    }
  };

  const handleInputChange = (e) => {
    const value = e.target.value;
    setLocalValue(value);
    
    // Clear any existing timeout
    if (debounceTimeoutRef.current) {
      clearTimeout(debounceTimeoutRef.current);
    }
    
    // For empty values, search immediately
    if (!value.trim()) {
      if (onSearch) {
        onSearch(value);
      }
      return;
    }
    
    // For short values, don't geocode yet (just update parent state immediately for text filtering)
    if (value.length < 3) {
      if (onSearch) {
        onSearch(value);
      }
      return;
    }
    
    // For longer values, debounce the geocoding
    debounceTimeoutRef.current = setTimeout(() => {
      if (onSearch) {
        onSearch(value);
      }
    }, 500); // 500ms delay for geocoding
  };

  const handleClear = () => {
    // Clear any pending debounced calls
    if (debounceTimeoutRef.current) {
      clearTimeout(debounceTimeoutRef.current);
    }
    
    setLocalValue('');
    if (onClear) {
      onClear();
    } else if (onSearch) {
      onSearch('');
    }
  };

  // Cleanup timeout on unmount
  useEffect(() => {
    return () => {
      if (debounceTimeoutRef.current) {
        clearTimeout(debounceTimeoutRef.current);
      }
    };
  }, []);

  return (
    <form className="search-form" onSubmit={handleSubmit}>
      <div className="search-container">
        <div className="search-input-wrapper">
          <Search className="search-icon" size={18} />
          <input
            type="text"
            className="search-input"
            placeholder={placeholder}
            value={localValue}
            onChange={handleInputChange}
            disabled={loading}
            autoComplete="off"
          />
          {loading && (
            <Loader className="search-loading" size={18} />
          )}
          {localValue && !loading && (
            <button
              type="button"
              className="search-clear"
              onClick={handleClear}
              aria-label="Clear search"
            >
              <X size={16} />
            </button>
          )}
        </div>
        <button 
          type="submit" 
          className="search-button"
          disabled={loading}
        >
          {loading ? 'Searching...' : 'Search'}
        </button>
      </div>
    </form>
  );
};

export default SearchBar;